/**
 * ROI Analysis Template Form
 * Return on investment calculations and analysis
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const ROIAnalysisForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '',
    company_name: '',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    currency: 'USD',
    analysis_period: '3 years',
    discount_rate: 10.0,
    investments: [{
      item: '', category: '', initial_investment: 0, ongoing_cost: 0, expected_benefit: 0,
      payback_period: '', roi_percentage: 0, npv: 0, irr: 0, risk_level: 'Medium', confidence: 'Medium', notes: ''
    }],
    benefits: [{ description: '', type: 'Revenue', year_1: 0, year_2: 0, year_3: 0 }],
    costs: [{ description: '', type: 'One-time', year_1: 0, year_2: 0, year_3: 0 }],
    assumptions: [''],
    risks: [''],
    recommendation: '',
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    project_name: 'CRM Platform Implementation',
    company_name: 'Acme Corp',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Finance Analyst',
    currency: 'USD',
    analysis_period: '3 years',
    discount_rate: 10.0,
    investments: [
      { item: 'Software License (Salesforce)', category: 'Software', initial_investment: 150000, ongoing_cost: 50000, expected_benefit: 200000, payback_period: '18 months', roi_percentage: 85, npv: 180000, irr: 28, risk_level: 'Low', confidence: 'High', notes: 'Proven platform' },
      { item: 'Implementation Services', category: 'Services', initial_investment: 75000, ongoing_cost: 0, expected_benefit: 0, payback_period: 'N/A', roi_percentage: 0, npv: 0, irr: 0, risk_level: 'Medium', confidence: 'High', notes: 'One-time cost' },
      { item: 'Training & Change Management', category: 'Training', initial_investment: 25000, ongoing_cost: 5000, expected_benefit: 50000, payback_period: '12 months', roi_percentage: 100, npv: 40000, irr: 35, risk_level: 'Low', confidence: 'Medium', notes: 'Productivity gains' },
      { item: 'Data Migration', category: 'Services', initial_investment: 30000, ongoing_cost: 0, expected_benefit: 0, payback_period: 'N/A', roi_percentage: 0, npv: 0, irr: 0, risk_level: 'Medium', confidence: 'Medium', notes: 'Clean data migration' }
    ],
    benefits: [
      { description: 'Increased Sales Revenue', type: 'Revenue', year_1: 100000, year_2: 200000, year_3: 300000 },
      { description: 'Improved Customer Retention', type: 'Revenue', year_1: 50000, year_2: 75000, year_3: 100000 },
      { description: 'Reduced Manual Data Entry', type: 'Cost Savings', year_1: 30000, year_2: 35000, year_3: 40000 },
      { description: 'Faster Sales Cycle', type: 'Productivity', year_1: 25000, year_2: 40000, year_3: 50000 }
    ],
    costs: [
      { description: 'Software Implementation', type: 'One-time', year_1: 280000, year_2: 0, year_3: 0 },
      { description: 'Annual Licensing', type: 'Recurring', year_1: 50000, year_2: 55000, year_3: 60000 },
      { description: 'Ongoing Support & Training', type: 'Recurring', year_1: 15000, year_2: 15000, year_3: 15000 },
      { description: 'Infrastructure Costs', type: 'Recurring', year_1: 10000, year_2: 12000, year_3: 14000 }
    ],
    assumptions: [
      'Sales team adoption rate of 85% within 6 months',
      'No major changes to business processes during implementation',
      'Existing IT infrastructure supports integration requirements',
      'Vendor pricing remains stable over analysis period',
      'Customer base grows at 10% annually'
    ],
    risks: [
      'User adoption may be slower than expected',
      'Integration complexity with legacy systems',
      'Data quality issues during migration',
      'Change in vendor pricing after Year 1'
    ],
    recommendation: 'Proceed with implementation. The projected ROI of 85% over 3 years justifies the initial investment. The payback period of 18 months is within acceptable range. Key success factors include strong change management and executive sponsorship.',
    rai_assignments: [
      { task: 'Finalize vendor contract', responsible: 'Procurement Lead', accountable: 'CFO', informed: 'IT Director, Sales VP', due_date: '2025-02-01', status: 'In Progress' },
      { task: 'Develop implementation timeline', responsible: 'Project Manager', accountable: 'CTO', informed: 'All stakeholders', due_date: '2025-02-15', status: 'Pending' }
    ],
    followups: [
      { item: 'Schedule board presentation', owner: 'CFO', due_date: '2025-01-20', priority: 'High', status: 'Pending' },
      { item: 'Complete security assessment', owner: 'IT Director', due_date: '2025-01-25', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "ROI Analysis Draft";
      const payload = {
        name: draftName,
        template_type: "ROI Analysis",
        template_id: "roi-analysis",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "r-o-i-analysis", "R O I Analysis", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("r-o-i-analysis", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/roi-analysis`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `ROI_Analysis_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "ROI Analysis",
        formData,
        settings: activeSettings || {},
        templateType: "ROI Analysis",
        templateId: "roi-analysis",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addInvestment = () => {
    setFormData({
      ...formData,
      investments: [...formData.investments, {
        item: '', category: '', initial_investment: 0, ongoing_cost: 0, expected_benefit: 0,
        payback_period: '', roi_percentage: 0, npv: 0, irr: 0, risk_level: 'Medium', confidence: 'Medium', notes: ''
      }]
    });
  };

  const updateInvestment = (index, field, value) => {
    const investments = [...formData.investments];
    investments[index] = { ...investments[index], [field]: value };
    setFormData({ ...formData, investments });
  };

  const removeInvestment = (index) => {
    setFormData({ ...formData, investments: formData.investments.filter((_, i) => i !== index) });
  };

  const addBenefit = () => {
    setFormData({ ...formData, benefits: [...formData.benefits, { description: '', type: 'Revenue', year_1: 0, year_2: 0, year_3: 0 }] });
  };

  const updateBenefit = (index, field, value) => {
    const benefits = [...formData.benefits];
    benefits[index] = { ...benefits[index], [field]: value };
    setFormData({ ...formData, benefits });
  };

  const addCost = () => {
    setFormData({ ...formData, costs: [...formData.costs, { description: '', type: 'One-time', year_1: 0, year_2: 0, year_3: 0 }] });
  };

  const updateCost = (index, field, value) => {
    const costs = [...formData.costs];
    costs[index] = { ...costs[index], [field]: value };
    setFormData({ ...formData, costs });
  };

  const categories = ['Software', 'Hardware', 'Services', 'Training', 'Infrastructure', 'Personnel', 'Other'];
  const riskLevels = ['Low', 'Medium', 'High'];
  const confidenceLevels = ['Low', 'Medium', 'High'];
  const benefitTypes = ['Revenue', 'Cost Savings', 'Productivity', 'Risk Reduction', 'Other'];
  const costTypes = ['One-time', 'Recurring', 'Variable'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">ROI Analysis</h1>
                <p className="text-sm text-gray-500">Return on investment calculations and analysis</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Analysis Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Company</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Analyst" /></div>
              <div><Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Analysis Period</Label><Input value={formData.analysis_period} onChange={(e) => setFormData({...formData, analysis_period: e.target.value})} placeholder="3 years" /></div>
              <div><Label>Discount Rate (%)</Label><Input type="number" value={formData.discount_rate} onChange={(e) => setFormData({...formData, discount_rate: parseFloat(e.target.value) || 0})} /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Investment Items</Label>
              <p className="text-xs text-gray-500 mb-3">List all investment components with their financial metrics</p>
              
              <div className="space-y-4">
                {formData.investments.map((inv, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between mb-3">
                      <span className="text-sm font-medium">Investment #{idx + 1}</span>
                      <Button variant="ghost" size="icon" onClick={() => removeInvestment(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="col-span-2"><Label className="text-xs">Item *</Label><Input value={inv.item} onChange={(e) => updateInvestment(idx, 'item', e.target.value)} placeholder="Investment item" className="text-sm" /></div>
                      <div><Label className="text-xs">Category</Label>
                        <Select value={inv.category} onValueChange={(v) => updateInvestment(idx, 'category', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Initial Investment</Label><Input type="number" value={inv.initial_investment} onChange={(e) => updateInvestment(idx, 'initial_investment', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
                      <div><Label className="text-xs">Ongoing Cost/yr</Label><Input type="number" value={inv.ongoing_cost} onChange={(e) => updateInvestment(idx, 'ongoing_cost', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Expected Benefit</Label><Input type="number" value={inv.expected_benefit} onChange={(e) => updateInvestment(idx, 'expected_benefit', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Payback Period</Label><Input value={inv.payback_period} onChange={(e) => updateInvestment(idx, 'payback_period', e.target.value)} placeholder="18 months" className="text-sm" /></div>
                      <div><Label className="text-xs">ROI %</Label><Input type="number" value={inv.roi_percentage} onChange={(e) => updateInvestment(idx, 'roi_percentage', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Risk Level</Label>
                        <Select value={inv.risk_level} onValueChange={(v) => updateInvestment(idx, 'risk_level', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{riskLevels.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Confidence</Label>
                        <Select value={inv.confidence} onValueChange={(v) => updateInvestment(idx, 'confidence', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{confidenceLevels.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addInvestment} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Investment</Button>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-4">
                <Label className="font-semibold text-green-800">Expected Benefits (3-Year Projection)</Label>
                <div className="space-y-2 mt-3">
                  {formData.benefits.map((b, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-end">
                      <div className="col-span-2"><Input value={b.description} onChange={(e) => updateBenefit(idx, 'description', e.target.value)} placeholder="Benefit" className="text-sm" /></div>
                      <Input type="number" value={b.year_1} onChange={(e) => updateBenefit(idx, 'year_1', parseFloat(e.target.value) || 0)} placeholder="Year 1" className="text-sm" />
                      <Input type="number" value={b.year_2} onChange={(e) => updateBenefit(idx, 'year_2', parseFloat(e.target.value) || 0)} placeholder="Year 2" className="text-sm" />
                      <Input type="number" value={b.year_3} onChange={(e) => updateBenefit(idx, 'year_3', parseFloat(e.target.value) || 0)} placeholder="Year 3" className="text-sm" />
                      <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, benefits: formData.benefits.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addBenefit} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <Label className="font-semibold text-red-800">Projected Costs (3-Year)</Label>
                <div className="space-y-2 mt-3">
                  {formData.costs.map((c, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-end">
                      <div className="col-span-2"><Input value={c.description} onChange={(e) => updateCost(idx, 'description', e.target.value)} placeholder="Cost item" className="text-sm" /></div>
                      <Input type="number" value={c.year_1} onChange={(e) => updateCost(idx, 'year_1', parseFloat(e.target.value) || 0)} placeholder="Year 1" className="text-sm" />
                      <Input type="number" value={c.year_2} onChange={(e) => updateCost(idx, 'year_2', parseFloat(e.target.value) || 0)} placeholder="Year 2" className="text-sm" />
                      <Input type="number" value={c.year_3} onChange={(e) => updateCost(idx, 'year_3', parseFloat(e.target.value) || 0)} placeholder="Year 3" className="text-sm" />
                      <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, costs: formData.costs.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addCost} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Key Assumptions</Label>
                {formData.assumptions.map((a, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={a} onChange={(e) => { const assumptions = [...formData.assumptions]; assumptions[idx] = e.target.value; setFormData({...formData, assumptions}); }} placeholder="Assumption" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, assumptions: formData.assumptions.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({...formData, assumptions: [...formData.assumptions, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              <div>
                <Label className="font-semibold">Risks</Label>
                {formData.risks.map((r, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={r} onChange={(e) => { const risks = [...formData.risks]; risks[idx] = e.target.value; setFormData({...formData, risks}); }} placeholder="Risk" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, risks: formData.risks.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({...formData, risks: [...formData.risks, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <div><Label className="font-semibold">Recommendation</Label><Textarea value={formData.recommendation} onChange={(e) => setFormData({...formData, recommendation: e.target.value})} placeholder="Your recommendation based on the analysis..." rows={3} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ROIAnalysisForm;
